import { useEffect, useState, useRef } from 'react';
import { Header } from './Header/Header';
import { S3Client } from '@aws-sdk/client-s3';
import { isNil } from 'lodash';
import { DynamoDBClient, ScanCommand, ScanCommandOutput, AttributeValue } from '@aws-sdk/client-dynamodb';
import { User } from '../models/User';
import { unmarshall } from '@aws-sdk/util-dynamodb';
import { LambdaClient } from '@aws-sdk/client-lambda';
import { LandingSection } from './LandingSection/LandingSection';
import { DocumentsSection } from './DocumentsSection/DocumentsSection';
import { PhotosSection } from './PhotosSection/PhotosSection';
import { ContactsSection } from './ContactsSection/ContactsSection';
import { VideosSection } from './VideosSection/VideosSection';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { Loading } from './Common';
import { XIcon } from '@heroicons/react/solid';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import logo from '../FamilyCouncilLogo.jpg';

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: 'us-east-1_8izkf6tti',
      userPoolClientId: '5atl4es9f61nkhjnckb4fua1qi',
      identityPoolId: 'us-east-1:9e954036-c53c-4016-b0c2-81d42cbb1a14',
    }
  }
});

const MainApp = ({ signOut, authUser }: { signOut?: () => void; authUser?: any }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPostCollege, setIsPostCollege] = useState(false);
  const [isAdvisor, setIsAdvisor] = useState(false);
  const [s3Client, setS3Client] = useState<S3Client>();
  const [dynamoDbClient, setdynamoDbClient] = useState<DynamoDBClient>();
  const [lambdaClient, setLambdaClient] = useState<LambdaClient>();
  const [users, setUsers] = useState<User[]>([]);
  const [user, setUser] = useState<User>();
  const [showAlert, setShowAlert] = useState(false);
  const [alertStatus, setAlertStatus] = useState<'SUCCESS' | 'ERROR' | undefined>(undefined);
  const [alertMessage, setAlertMessage] = useState<string>();
  const [alertSubMessages, setAlertSubMessages] = useState<string[]>([]);
  const alertTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isContactsLoaded, setIsContactsLoaded] = useState(false);

  // Function to re-fetch all users and update state
  // It accepts optional initial clients for the first load, otherwise uses state
  const fetchUsersAndSetState = async (initialDdbClient?: DynamoDBClient, initialUsername?: string) => {
    const client = initialDdbClient || dynamoDbClient;
    const currentUsername = initialUsername || authUser?.username;
    if (!client || !currentUsername) return; // Ensure clients and user are available

    try {
      setIsContactsLoaded(false);
      let lastEvaluatedKey: Record<string, AttributeValue> | undefined = undefined;
      const allUsers: User[] = [];
      do {
        const command: ScanCommand = new ScanCommand({ TableName: 'MitchellFC', ExclusiveStartKey: lastEvaluatedKey });
        const output: ScanCommandOutput = await client.send(command);
        if (output.Items) {
          allUsers.push(...output.Items.map((item: Record<string, AttributeValue>) => unmarshall(item) as User));
        }
        lastEvaluatedKey = output.LastEvaluatedKey;
      } while (lastEvaluatedKey);

      if (allUsers.length > 0) {
        const userFromList = allUsers.filter((u) => u.cognitoUserId === currentUsername)[0];
        setUser(userFromList); // Update the current user state
        setUsers(allUsers); // Update all users/contacts state
        const html = document.querySelector('html');
        localStorage.setItem('darkMode', userFromList.darkMode.toString());
        userFromList.darkMode ? html?.classList.add('dark') : html?.classList.remove('dark');
      }
    } catch (error) {
      console.error("Failed to fetch users from DynamoDB:", error);
      alert("There was an error connecting to the database. Please refresh the page.");
    } finally {
      setIsContactsLoaded(true);
    }
  };

  // Function to display alerts on the main page
  const onShowAlert = (status: 'SUCCESS' | 'ERROR', message: string, subMessages: string[] = []) => {
    setAlertStatus(status);
    setAlertMessage(message);
    setAlertSubMessages(subMessages);
    // Clear any existing timeout before setting a new alert
    if (alertTimeoutRef.current) {
      clearTimeout(alertTimeoutRef.current);
    }
    setShowAlert(true);
  };

  useEffect(() => {
    const initApp = async () => {
      try {
        const session = await fetchAuthSession();
        const groups = (session.tokens?.accessToken?.payload?.['cognito:groups'] as string[]) || [];
        
        if (groups.includes('Admins')) setIsAdmin(true);
        if (groups.includes('Advisors')) setIsAdvisor(true);
        if (groups.includes('Post_College')) setIsPostCollege(true);

        const credentialProvider = async () => {
          const { credentials } = await fetchAuthSession();
          if (!credentials) throw new Error('No credentials');
          return credentials;
        };

        const clientConfig = {
          region: 'us-east-1',
          maxAttempts: 5,
          credentials: credentialProvider,
        };

        const ddbClient = new DynamoDBClient(clientConfig);
        setLambdaClient(new LambdaClient(clientConfig));
        setS3Client(new S3Client(clientConfig));
        setdynamoDbClient(ddbClient);

        // Do not await this! Let it run in the background so the UI renders instantly.
        fetchUsersAndSetState(ddbClient, authUser?.username);
      } catch (error) {
        console.error('Failed to initialize app:', error);
        onShowAlert('ERROR', 'Failed to authenticate and load data. Please refresh.');
      }
    };

    initApp();
  }, [authUser]);

  // Effect for auto-dismissal of the alert
  useEffect(() => {
    if (showAlert) {
      alertTimeoutRef.current = setTimeout(() => {
        setShowAlert(false);
      }, 5000); // Disappear after 5 seconds

      // Cleanup function to clear timeout if component unmounts or alert is manually dismissed
      return () => {
        if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current);
      };
    }
  }, [showAlert]); // Re-run when showAlert changes

  const isMobile = window.innerWidth < 768;

  // Variants for staggered entrance and scroll reveals
  const sectionVariants: Variants = {
    hidden: { opacity: 0, y: isMobile ? 0 : 20 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.6, ease: 'easeOut' } 
    },
  };
  
  // Variants for the toast notification animation
  const toastVariants: Variants = {
    hidden: { opacity: 0, y: 50, x: 0, scale: 0.9 },
    visible: { opacity: 1, y: 0, x: 0, scale: 1, transition: { duration: 0.3, ease: "easeOut" } },
    exit: { opacity: 0, y: 50, x: 0, scale: 0.9, transition: { duration: 0.2, ease: "easeIn" } },
  };

  return (
    <motion.div
      key="content"
      initial="hidden"
      animate="visible"
      className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors duration-500"
    >
      <Header 
        dynamoDbClient={dynamoDbClient as DynamoDBClient}
        user={user as User} 
        signOut={signOut!} 
            onSettingsSaved={fetchUsersAndSetState} 
            onShowAlert={onShowAlert} // Pass the new alert handler
          />

          <AnimatePresence> {/* New AnimatePresence for the toast */}
            {showAlert && (
            <div
                className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[1000] w-full max-w-xs sm:max-w-sm px-4 sm:px-0"
            >
                <motion.div
                  key="toast-alert"
                  variants={toastVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className={`rounded-lg shadow-lg p-4 pr-10 transition-colors duration-300
                    ${alertStatus === 'SUCCESS' ? 'bg-green-100 border border-green-400 text-green-700' : 'bg-red-100 border border-red-400 text-red-700'}
                  `}
                  role="alert"
                >
                  <strong className="font-bold">{alertStatus === 'SUCCESS' ? 'Success! ' : 'Error! '}</strong>
                  <span className="block sm:inline">{alertMessage}</span>
                  <button onClick={() => setShowAlert(false)} className="absolute top-2 right-2 p-1 rounded-full hover:bg-opacity-75 focus:outline-none focus:ring-2 focus:ring-offset-2">
                    <XIcon className={`h-5 w-5 ${alertStatus === 'SUCCESS' ? 'text-green-700' : 'text-red-700'}`} aria-hidden="true" />
                  </button>
                  {alertSubMessages.length > 0 && <ul className="list-disc ml-6 mt-2 text-sm">{alertSubMessages.map((msg, i) => <li key={i}>{msg}</li>)}</ul>}
                </motion.div>
              </div>
            )}
          </AnimatePresence>
          
          <div id="home" className="">
            <motion.div variants={sectionVariants}>
              <LandingSection />
            </motion.div>
          </div>

          <div id="documentDisplayer" className="">
            <motion.div variants={sectionVariants} whileInView="visible" initial="hidden" viewport={{ once: true, margin: "-100px" }}>
              <DocumentsSection 
                lambdaClient={lambdaClient as LambdaClient}
                s3Client={s3Client as S3Client}
                isAdmin={isAdmin}
                isPostCollege={isPostCollege}
                isAdvisor={isAdvisor}
              />
            </motion.div>
          </div>

          <div id="contacts" className="">
            <motion.div variants={sectionVariants} whileInView="visible" initial="hidden" viewport={{ once: true, margin: "-100px" }}>
              <ContactsSection users={users} isAdmin={isAdmin} isContactsLoaded={isContactsLoaded} />
            </motion.div>
          </div>

          <div id="photos" className="">
            <motion.div variants={sectionVariants} whileInView="visible" initial="hidden" viewport={{ once: true, margin: "-100px" }}>
              <PhotosSection />
            </motion.div>
          </div>

          <div id="videos" className="">
            <motion.div variants={sectionVariants} whileInView="visible" initial="hidden" viewport={{ once: true, margin: "-100px" }}>
              <VideosSection />
            </motion.div>
          </div>
    </motion.div>
  );
};

const components = {
  Header() {
    const isMobile = window.innerWidth < 768;
    return (
      <motion.div 
        initial={{ opacity: 0, y: isMobile ? 0 : 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        className="text-center pb-8 pt-6 flex flex-col items-center"
      >
        <motion.div 
          initial={{ scale: isMobile ? 1 : 0.85, opacity: 0, y: isMobile ? 0 : 15 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          className="mb-8 relative group cursor-default"
        >
          <div className="relative w-36 sm:w-44 p-4 sm:p-5 rounded-[2.5rem] shadow-[0_15px_40px_-10px_rgba(59,130,246,0.4)] dark:shadow-[0_15px_40px_-10px_rgba(59,130,246,0.2)] border border-slate-200/50 dark:border-slate-700/50 overflow-hidden bg-white flex items-center justify-center transform transition-all duration-700 hover:scale-105 hover:shadow-[0_20px_50px_-10px_rgba(59,130,246,0.6)] dark:hover:shadow-[0_20px_50px_-10px_rgba(59,130,246,0.3)]">
            <img
              className="w-full h-auto block"
              src={logo}
              alt="Mitchell Family Logo"
            />
          </div>
        </motion.div>
        <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
          Mitchell Family Council
        </h2>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Sign in to access documents and more!
        </p>
      </motion.div>
    );
  },
};

const AuthWrapper = () => {
  const { route, user, signOut } = useAuthenticator((context) => [
    context.route,
    context.user,
  ]);

  // Set the theme as early as possible to prevent a flash of the wrong theme.
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    const html = document.querySelector('html');
    if (savedDarkMode === 'true') {
      html?.classList.add('dark');
    } else {
      html?.classList.remove('dark');
    }
  }, []);

  const isMobile = window.innerWidth < 768;

  if (route === 'authenticated') {
    return <MainApp signOut={signOut} authUser={user} />;
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center px-4 sm:px-6 lg:px-8 relative">
      <motion.div 
        initial={{ opacity: 0, y: isMobile ? 0 : 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
        className="w-full max-w-md"
      >
        <style>
          {`
            [data-amplify-authenticator] {
              --amplify-colors-brand-primary-80: #2563eb;
              --amplify-colors-brand-primary-90: #1d4ed8;
              --amplify-colors-brand-primary-100: #1e3a8a;
              --amplify-components-authenticator-router-box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.15), 0 0 0 1px rgb(0 0 0 / 0.05);
              --amplify-components-authenticator-router-border-width: 0;
              --amplify-components-authenticator-router-border-radius: 1.5rem;
              --amplify-radii-medium: 1rem;
              --amplify-components-fieldcontrol-border-radius: 0.75rem;
              --amplify-components-button-border-radius: 0.75rem;
              --amplify-components-fieldcontrol-border-color: #e2e8f0;
              --amplify-components-fieldcontrol-focus-box-shadow: 0 0 0 2px #bfdbfe;
            }
            [data-amplify-authenticator] [data-amplify-router] {
              border-radius: 1.5rem !important;
              overflow: hidden !important;
            }
            .dark [data-amplify-authenticator] {
              --amplify-colors-background-primary: #1e293b;
              --amplify-colors-background-secondary: #0f172a;
              --amplify-components-authenticator-router-background-color: #1e293b;
              --amplify-components-authenticator-router-box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.5), 0 0 0 1px rgb(255 255 255 / 0.1);
              --amplify-components-fieldcontrol-background-color: #0f172a;
              --amplify-components-fieldcontrol-border-color: #334155;
              --amplify-components-fieldcontrol-color: #f8fafc;
              --amplify-components-fieldcontrol-focus-box-shadow: 0 0 0 2px #3b82f6;
              --amplify-colors-font-primary: #f8fafc;
              --amplify-colors-font-secondary: #94a3b8;
              --amplify-colors-font-interactive: #e2e8f0;
              --amplify-colors-border-primary: #334155;
              --amplify-colors-border-focus: #3b82f6;
            }
            .dark .amplify-input {
              color: #f8fafc !important;
              background-color: #0f172a !important;
              border-color: #334155 !important;
            }
            .dark .amplify-input::placeholder {
              color: #64748b !important;
            }
            .dark .amplify-input:focus {
              border-color: #3b82f6 !important;
              box-shadow: 0 0 0 1px #3b82f6 !important;
            }
            .dark .amplify-input:-webkit-autofill,
            .dark .amplify-input:-webkit-autofill:hover, 
            .dark .amplify-input:-webkit-autofill:focus, 
            .dark .amplify-input:-webkit-autofill:active {
              -webkit-box-shadow: 0 0 0 30px #0f172a inset !important;
              -webkit-text-fill-color: #f8fafc !important;
            }
            .dark .amplify-label {
              color: #cbd5e1 !important;
            }
            .dark .amplify-field-group__control {
              color: #94a3b8 !important;
              background-color: transparent !important;
              border-color: #334155 !important;
            }
            .dark .amplify-field-group__control:hover {
              color: #f8fafc !important;
              background-color: #1e293b !important;
            }
            .amplify-button--primary {
              background-color: var(--amplify-colors-brand-primary-80) !important;
              color: white !important;
              box-shadow: 0 4px 6px -1px rgb(37 99 235 / 0.3), 0 2px 4px -2px rgb(37 99 235 / 0.3) !important;
              transition: all 0.2s ease-in-out !important;
            }
            .amplify-button--primary:hover {
              background-color: var(--amplify-colors-brand-primary-90) !important;
              transform: translateY(-1px) !important;
              box-shadow: 0 6px 8px -1px rgb(37 99 235 / 0.4), 0 4px 6px -2px rgb(37 99 235 / 0.4) !important;
            }
            .amplify-button--link {
              color: var(--amplify-colors-brand-primary-80) !important;
            }
            .dark .amplify-button--primary {
              box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.4), 0 2px 4px -2px rgb(0 0 0 / 0.4) !important;
            }
            .dark .amplify-button--primary:hover {
              box-shadow: 0 6px 8px -1px rgb(0 0 0 / 0.6), 0 4px 6px -2px rgb(0 0 0 / 0.6) !important;
            }
            .dark .amplify-button--link {
              color: #60a5fa !important;
            }
          `}
        </style>
        <Authenticator hideSignUp loginMechanisms={['email']} components={components} />
      </motion.div>
    </div>
  );
};

export default function SinglePage() {
  return (
    <Authenticator.Provider>
      <AuthWrapper />
    </Authenticator.Provider>
  );
}
